import { useEffect, useState } from 'react';
import { TodoList } from '../../types/todo';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { Icon } from '../../utils/icons';

interface ListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; theme?: string; icon?: string }) => Promise<void>;
  editingList?: TodoList | null;
  isLoading?: boolean;
}

export default function ListModal({
  isOpen,
  onClose,
  onSubmit,
  editingList,
  isLoading = false,
}: ListModalProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);

  const [name, setName] = useState('');
  const [theme, setTheme] = useState('');
  const [icon, setIcon] = useState('');

  // Pre-fill form when editing
  useEffect(() => {
    if (editingList) {
      setName(editingList.name);
      setTheme(editingList.theme || '');
      setIcon(editingList.icon || '');
    } else {
      // Reset form for new list
      setName('');
      setTheme('');
      setIcon('');
    }
  }, [editingList, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    await onSubmit({
      name: name.trim(),
      theme: theme.trim() || undefined,
      icon: icon.trim() || undefined,
    });
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative ${tokens.card.bg} rounded-landing ${tokens.card.shadow} max-w-md w-full max-h-[90vh] overflow-hidden flex flex-col z-10 ${tokens.border.light} border`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${tokens.border.light}`}>
          <h2 className={`text-xl font-bold ${tokens.text.primary}`}>
            {editingList ? 'Edit List' : 'Create List'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Name */}
          <div>
            <label htmlFor="name" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
              Name *
            </label>
            <input
              type="text"
              id="name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing ${tokens.card.bg} ${tokens.text.primary} focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent`}
              placeholder="List name"
              autoFocus
            />
          </div>

          {/* Theme */}
          <div>
            <label htmlFor="theme" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
              Theme (optional)
            </label>
            <input
              type="text"
              id="theme"
              value={theme}
              onChange={(e) => setTheme(e.target.value)}
              className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing ${tokens.card.bg} ${tokens.text.primary} focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent`}
              placeholder="e.g., Work, Personal, Shopping"
            />
          </div>

          {/* Icon */}
          <div>
            <label htmlFor="icon" className={`block text-sm font-medium ${tokens.text.primary} mb-1`}>
              Icon (optional)
            </label>
            <input
              type="text"
              id="icon"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              className={`w-full px-3 py-2 ${tokens.border.light} border rounded-landing ${tokens.card.bg} ${tokens.text.primary} focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent`}
              placeholder="Icon name or emoji"
            />
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 ${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary} font-semibold py-2.5 px-4 rounded-landing transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !name.trim()}
              className={`flex-1 ${tokens.button.primary} text-white font-semibold py-2.5 px-4 rounded-landing transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? 'Saving...' : editingList ? 'Save Changes' : 'Create List'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
